import React from 'react';
import { Search as SearchIcon, SlidersHorizontal, Download, Trash2, Loader2 } from 'lucide-react';
import { STATUS_LABELS, capitalize } from '../utils/proposalHelpers';

/**
 * MyProposalsFilterBar — extracted from MyProposals.jsx (Phase 7 refactor).
 * Renders the search input, main status pills, and "More Filters" dropdown.
 * Zero logic changes. All state/handlers passed as props from parent.
 */
const MyProposalsFilterBar = ({
    searchQuery,
    setSearchQuery,
    setCurrentPage,
    statusFilter,
    setStatusFilter,
    rfpStats,
    showMoreFilters,
    setShowMoreFilters,
    moreFiltersRef,
    selectedCount = 0,
    onDownloadBulk,
    onDeleteBulk,
    isBulkDownloading = false,
    isManager = false
}) => {
    // Explicitly hardcode all filter keys to ensure they are always displayed
    const allFilterKeys = ['all', 'analysis', 'review', 'submitted', 'finalised', 'accepted', 'rejected', 'due_this_week'];

    return (
        <div className="table-filter-bar" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="search-wrapper" style={{ flex: 1 }}>
                <SearchIcon size={18} className="search-icon" />
                <input
                    type="text"
                    placeholder="Search proposals..."
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                    }}
                />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isManager && (
                    <div className="filter-dropdown-container" ref={moreFiltersRef}>
                        <button
                            className={`filter-dropdown-btn ${statusFilter !== 'all' ? 'active' : ''}`}
                            onClick={() => setShowMoreFilters(!showMoreFilters)}
                        >
                            <SlidersHorizontal size={14} />
                            <span>{STATUS_LABELS[statusFilter] || 'Filters'}</span>
                        </button>

                        {showMoreFilters && (
                            <div className="filter-dropdown-menu">
                                {allFilterKeys.map(key => {
                                    const isActive = statusFilter === key;
                                    return (
                                        <button
                                            key={key}
                                            className={`dropdown-item ${isActive ? 'active' : ''}`}
                                            onClick={() => {
                                                setStatusFilter(key);
                                                setCurrentPage(1);
                                                setShowMoreFilters(false);
                                            }}
                                        >
                                            <span className="item-label">{STATUS_LABELS[key] || capitalize(key)}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {selectedCount > 0 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        animation: 'fadeIn 0.2s ease-in-out'
                    }}>
                        <button
                            type="button"
                            onClick={onDownloadBulk}
                            disabled={isBulkDownloading}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#FFF0EB',
                                border: '1px solid #FFD4C6',
                                borderRadius: '8px',
                                color: '#E04E1E',
                                padding: '8px 14px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: isBulkDownloading ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                height: '38px',
                                boxSizing: 'border-box'
                            }}
                            onMouseEnter={(e) => {
                                if (!isBulkDownloading) {
                                    e.currentTarget.style.background = '#FFE0D5';
                                    e.currentTarget.style.borderColor = '#FF5622';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isBulkDownloading) {
                                    e.currentTarget.style.background = '#FFF0EB';
                                    e.currentTarget.style.borderColor = '#FFD4C6';
                                }
                            }}
                        >
                            {isBulkDownloading ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Download size={14} />
                            )}
                            <span>{isBulkDownloading ? 'Downloading...' : `Download (${selectedCount})`}</span>
                        </button>

                        <button
                            type="button"
                            onClick={onDeleteBulk}
                            disabled={isBulkDownloading}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: isBulkDownloading ? '#F3F4F6' : '#FEF2F2',
                                border: isBulkDownloading ? '1px solid #E5E7EB' : '1px solid #FCA5A5',
                                borderRadius: '8px',
                                color: isBulkDownloading ? '#9CA3AF' : '#EF4444',
                                padding: '8px 14px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: isBulkDownloading ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                height: '38px',
                                boxSizing: 'border-box'
                            }}
                            onMouseEnter={(e) => {
                                if (!isBulkDownloading) {
                                    e.currentTarget.style.background = '#FEE2E2';
                                    e.currentTarget.style.borderColor = '#EF4444';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isBulkDownloading) {
                                    e.currentTarget.style.background = '#FEF2F2';
                                    e.currentTarget.style.borderColor = '#FCA5A5';
                                }
                            }}
                        >
                            <Trash2 size={14} />
                            <span>Delete ({selectedCount})</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyProposalsFilterBar;
